import { FILE_TYPE_STYLES } from "@/lib/file-icons";
import type { FileType } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Donut chart of documents by file_type. Hand-rolled with SVG arcs so
 * the bundle doesn't pay for a chart library. Slice order matches the
 * legend below.
 */

const TYPES: { value: FileType; label: string; color: string }[] = [
  { value: "document", label: "Documents", color: "#0ea5a4" }, // teal-ish (primary-aligned)
  { value: "video",    label: "Videos",    color: "#f59e0b" }, // amber
  { value: "audio",    label: "Audio",     color: "#8b5cf6" }, // violet
  { value: "image",    label: "Images",    color: "#ec4899" }, // pink
  { value: "other",    label: "Other",     color: "#64748b" }, // slate
];

interface FileTypeDonutProps {
  data: Partial<Record<FileType, number>> | undefined;
  loading?: boolean;
}

export function FileTypeDonut({ data, loading }: FileTypeDonutProps) {
  const counts = TYPES.map((t) => ({ ...t, n: data?.[t.value] ?? 0 }));
  const total  = counts.reduce((s, t) => s + t.n, 0);

  // SVG geometry: 100x100 viewBox, donut on a circle of radius 40.
  const R = 40;
  const C = 2 * Math.PI * R;

  return (
    <div className="grid grid-cols-[7rem_1fr] items-center gap-5">
      <div className="relative h-28 w-28">
        <svg viewBox="0 0 100 100" className="-rotate-90">
          {/* Track */}
          <circle cx="50" cy="50" r={R} fill="none" stroke="hsl(var(--muted))" strokeWidth="14" />
          {/* Slices — render only when there's data so SVG stays clean. */}
          {!loading && total > 0 && (() => {
            let offset = 0;
            return counts.filter((t) => t.n > 0).map((t) => {
              const portion = t.n / total;
              const dash    = portion * C;
              const arc = (
                <circle
                  key={t.value}
                  cx="50" cy="50" r={R} fill="none"
                  stroke={t.color} strokeWidth="14"
                  strokeDasharray={`${dash} ${C - dash}`}
                  strokeDashoffset={-offset}
                />
              );
              offset += dash;
              return arc;
            });
          })()}
        </svg>
        {/* Center label */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className={cn(
            "font-mono text-xl font-bold tabular-nums tracking-tight",
            loading && "opacity-30",
          )}>
            {total.toLocaleString()}
          </div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Total
          </div>
        </div>
      </div>

      <ul className="space-y-1.5 text-xs">
        {counts.map((t) => {
          const Icon = FILE_TYPE_STYLES[t.value].icon;
          const pct = total > 0 ? Math.round((t.n / total) * 100) : 0;
          return (
            <li key={t.value} className="flex items-center gap-2">
              <span
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: t.color }}
                aria-hidden
              />
              <Icon className="h-3 w-3 text-muted-foreground" />
              <span className="flex-1 truncate text-muted-foreground">{t.label}</span>
              <span className="font-mono tabular-nums text-foreground">{t.n}</span>
              <span className="w-8 text-right font-mono tabular-nums text-muted-foreground">
                {pct}%
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
